const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function parseDisplayDate(s: string): Date | null {
  const m = s.match(/^(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/);
  if (!m) return null;
  const idx = MONTHS.indexOf(m[2]);
  if (idx < 0) return null;
  const d = new Date(Number(m[3]), idx, Number(m[1]));
  return isNaN(d.getTime()) ? null : d;
}

export function addMonthsToDate(s: string, months: number): string {
  const d = parseDisplayDate(s);
  if (!d) return "";
  d.setMonth(d.getMonth() + months);
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export interface EmiResult {
  financed: number;
  interest: number;
  payable: number;
  emi: number;
}

export function computeEmi(
  priceStr: string,
  downStr: string,
  rateStr: string,
  tenureStr: string,
): EmiResult | null {
  const price = Number(priceStr);
  const down = Number(downStr);
  const rate = Number(rateStr);
  const tenure = Number(tenureStr);
  if (!priceStr || !downStr || !rateStr || !tenureStr) return null;
  if (![price, down, rate, tenure].every((n) => Number.isFinite(n))) return null;
  if (price <= 0 || down < 0 || rate < 0 || tenure <= 0) return null;
  if (down >= price) return null;
  const financed = price - down;
  const interest = financed * (rate / 100) * (tenure / 12);
  const payable = financed + interest;
  const emi = payable / tenure;
  return { financed, interest, payable, emi };
}

export function roundUpToHundred(n: number): number {
  return Math.ceil(n / 100) * 100;
}

export function formatMoney(n: number): string {
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function isDateToday(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  const MONTHS_MAP: Record<string, number> = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };

  let d: Date | null = null;
  const s = String(dateStr).trim();

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const parts = s.split("-");
    d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  } else {
    // DD Mon YYYY
    const parts = s.split(/[\s-]+/);
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const mIdx = MONTHS_MAP[parts[1].slice(0, 3).toLowerCase()];
      const yr = parseInt(parts[2], 10);
      if (!isNaN(day) && mIdx !== undefined && !isNaN(yr)) {
        d = new Date(yr, mIdx, day);
      }
    }
  }

  if (!d || isNaN(d.getTime())) {
    const direct = new Date(s);
    if (!isNaN(direct.getTime())) d = direct;
    else return false;
  }

  const dFmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);

  const todayFmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  return dFmt === todayFmt;
}